import FileOfflineButton from "../components/FileOfflineButton";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { isHorizontalScrollZone } from "@/lib/swipe";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Music2, Plus, Pencil, Trash2, FileText, Video, Link2, Heart,
  Search, ChevronLeft, ChevronDown, Tag, Eye, ExternalLink, X, Maximize2,
  Download, Share2, Printer,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MediaPlayer from "@/components/MediaPlayer";
import { PieceFormSheet } from "@/components/PieceFormSheet";
import { presetPupitres, pupitreChoices } from "@/lib/pupitres";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

const FILE_ICONS = { partition: FileText, audio: Music2, video: Video, link: Link2 };
const KIND_CHIP = {
  partition: "bg-red-500/15 text-red-400",
  audio: "bg-amber-500/15 text-amber-400",
  video: "bg-sky-500/15 text-sky-400",
  link: "bg-slate-500/15 text-slate-400",
};
const FILE_CLASSES = [
  { kind: "partition", label: "lib.class_partition", icon: FileText, accept: ".pdf,.jpg,.jpeg,.png,.svg,.eps" },
  { kind: "audio", label: "lib.class_audio", icon: Music2, accept: ".mp3,.aac,.m4a,.wav,.aiff,.aif" },
  { kind: "video", label: "lib.class_video", icon: Video, accept: ".mp4,.mov,.avi,.mkv,.wmv,.webm" },
  { kind: "link", label: "lib.class_link", icon: Link2, accept: "" },
];
const fmtSize = (s, lang) => (!s ? "" : s >= 1048576 ? `${(s / 1048576).toFixed(1)} ${lang === "en" ? "MB" : "Mo"}` : s >= 1024 ? `${Math.round(s / 1024)} ${lang === "en" ? "KB" : "Ko"}` : `${s} ${lang === "en" ? "B" : "o"}`);

export default function LibraryPage() {
  const { user, group, t, lang } = useAuth();
  const playerCtx = usePlayer();
  const canWrite = ["admin", "music_leader"].includes(user?.role);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [nodes, setNodes] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [categoryId, setCategoryId] = useState("all");
  const [highlightId, setHighlightId] = useState(null);
  const [pieceId, setPieceId] = useState(null);
  const [versionId, setVersionId] = useState(null);
  const [filesByPupitre, setFilesByPupitre] = useState({});
  const [preview, setPreview] = useState(null);
  const [player, setPlayer] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [search, setSearch] = useState("");
  const [favorites, setFavorites] = useState({});
  const [dragOver, setDragOver] = useState(null);

  const [catOpen, setCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatDisc, setNewCatDisc] = useState("");
  const [mobDisc, setMobDisc] = useState("all");
  const [pieceOpen, setPieceOpen] = useState(false);
  const [editingPiece, setEditingPiece] = useState(null); // null = création, noeud = édition
  const [pieceCat, setPieceCat] = useState("");
  const [pupitreId, setPupitreId] = useState(null); // onglet pupitre sélectionné
  const [pupOpen, setPupOpen] = useState(false);
  const [pupName, setPupName] = useState("");
  const [verPupCustom, setVerPupCustom] = useState("");
  const [verOpen, setVerOpen] = useState(false);
  const [verName, setVerName] = useState("");
  const [verPupitres, setVerPupitres] = useState([]);
  const [renameNode, setRenameNode] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [renameFile, setRenameFile] = useState(null);
  const [renameFileVal, setRenameFileVal] = useState("");
  const [pendingUpload, setPendingUpload] = useState(null);
  const [uploadProg, setUploadProg] = useState(null);
  const [delNode, setDelNode] = useState(null);
  const [linkFor, setLinkFor] = useState(null);
  const [linkName, setLinkName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const loadTree = useCallback(() => {
    setLoadingTree(true);
    api.get("/library/tree")
      .then((r) => setNodes(r.data))
      .catch((e) => toast.error(e.friendly))
      .finally(() => setLoadingTree(false));
  }, []);

  const loadFavs = useCallback(() => {
    api.get("/favorites").then((r) => {
      const m = {};
      r.data.forEach((f) => { m[f.target_id] = f.id; });
      setFavorites(m);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadTree(); loadFavs(); }, [loadTree, loadFavs]);

  const nodeMap = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);
  const childrenOf = useMemo(() => {
    const m = {};
    nodes.forEach((n) => {
      const p = n.parent_id || "root";
      (m[p] = m[p] || []).push(n);
    });
    return m;
  }, [nodes]);
  const categories = (childrenOf["root"] || []).slice().sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }));
  // Disciplines du groupe : zones Bagad / Pipe Band affichées seulement si les deux sont pratiquées
  const disciplines = group?.disciplines || [];
  const bothDisc = disciplines.includes("bagad") && disciplines.includes("pipeband");
  const catsFor = (d) => categories.filter((c) => !c.discipline || c.discipline === d);
  const allPieces = useMemo(() => nodes.filter((n) => n.kind === "piece"), [nodes]);

  // Ouverture directe via ?node= (favoris, récents, agenda)
  useEffect(() => {
    const nid = params.get("node");
    if (!nid || !nodes.length) return;
    const n = nodeMap[nid];
    if (!n) return;
    if (n.kind === "category") {
      setCategoryId(n.id);
      setPieceId(null);
    } else if (params.get("open")) {
      // Avec ?open= : ouvrir la fiche du morceau (auto-ouverture du fichier)
      let v = null;
      let p = null;
      if (n.kind === "piece") p = n;
      else if (n.kind === "version") { v = n; p = nodeMap[n.parent_id]; }
      else if (n.kind === "pupitre") { v = nodeMap[n.parent_id]; p = v ? nodeMap[v.parent_id] : null; }
      if (p) {
        setPieceId(p.id);
        if (v) setVersionId(v.id);
      }
    } else {
      // Sans ?open= : chemin dans l'arborescence — catégorie sélectionnée + morceau surligné
      let p = null;
      if (n.kind === "piece") p = n;
      else if (n.kind === "version") p = nodeMap[n.parent_id];
      else if (n.kind === "pupitre") { const v = nodeMap[n.parent_id]; p = v ? nodeMap[v.parent_id] : null; }
      if (p) {
        setPieceId(null);
        setCategoryId(p.parent_id || "all");
        setHighlightId(p.id);
        setTimeout(() => {
          document.querySelector(`[data-testid="piece-item-${p.id}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
        }, 150);
      }
    }
    params.delete("node");
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes]);

  const versions = pieceId ? childrenOf[pieceId] || [] : [];
  useEffect(() => {
    if (pieceId && versions.length && !versions.find((v) => v.id === versionId)) {
      setVersionId(versions[versions.length - 1].id);
    }
    if (pieceId && !versions.length) setVersionId(null);
  }, [pieceId, versions, versionId]);

  const pupitres = versionId ? childrenOf[versionId] || [] : [];
  const pup = pupitres.find((p) => p.id === pupitreId) || null;

  useEffect(() => {
    if (!pupitres.length) { if (pupitreId) setPupitreId(null); return; }
    if (!pupitres.find((p) => p.id === pupitreId)) setPupitreId(pupitres[0].id);
  }, [pupitres, pupitreId]);

  useEffect(() => {
    if (!pupitres.length) { setFilesByPupitre({}); return; }
    Promise.all(
      pupitres.map((p) => api.get(`/files?node_id=${p.id}`).then((r) => [p.id, r.data]).catch(() => [p.id, []]))
    ).then((entries) => setFilesByPupitre(Object.fromEntries(entries)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);

  const openFile = useCallback((f) => {
    api.post(`/files/${f.id}/open`).catch(() => {});
    if (f.kind === "link") { window.open(f.url, "_blank"); return; }
    if ((f.name || "").toLowerCase().endsWith(".eps")) { window.open(fileUrl(f.id, "download=1"), "_blank"); return; }
    if (window.innerWidth >= 1280) { setPreview(f); return; }
    if (f.kind === "partition") { setPdf(f); return; }
    // BPM du morceau parent (pupitre → version → morceau) pour le badge du lecteur mobile
    const pupN = nodeMap[f.node_id];
    const verN = pupN ? nodeMap[pupN.parent_id] : null;
    const pieceN = verN ? nodeMap[verN.parent_id] : null;
    const withTempo = { ...f, pieceTempo: pieceN?.tempo_bpm || null };
    // Audio mobile : lecteur global persistant (arrière-plan + mini-barre + swipe pour minimiser)
    if (f.kind === "audio" && playerCtx && window.matchMedia("(max-width: 1023px)").matches) { playerCtx.open(withTempo); return; }
    setPlayer(withTempo);
  }, [nodeMap, playerCtx]);

  // Auto-ouverture via ?open=
  useEffect(() => {
    const oid = params.get("open");
    if (!oid) return;
    const all = Object.values(filesByPupitre).flat();
    const f = all.find((x) => x.id === oid);
    if (f) {
      openFile(f);
      params.delete("open");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesByPupitre]);

  // Échap ferme le lecteur PDF modal même si le focus est dans l'iframe
  useEffect(() => {
    if (!pdf) return;
    const onKey = (e) => { if (e.key === "Escape") setPdf(null); };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [pdf]);

  // Plein écran pour les visuels (PDF/JPG/PNG…) — repli nouvel onglet sur iOS
  const viewerFullscreen = (testid, f) => {
    if (document.fullscreenElement) { document.exitFullscreen(); return; }
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el && el.requestFullscreen) {
      el.requestFullscreen().catch(() => window.open(fileUrl(f.id), "_blank"));
    } else {
      window.open(fileUrl(f.id), "_blank");
    }
  };

  // Fermeture du lecteur PDF : sort d'abord du plein écran natif si actif
  const closePdf = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    setPdf(null);
  };

  const piecesList = useMemo(() => allPieces
    .filter((p) => categoryId === "all" || p.parent_id === categoryId)
    .filter((p) => !search.trim() || p.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" })), [allPieces, categoryId, search]);

  const piece = pieceId ? nodeMap[pieceId] : null;
  const pieceCategory = piece?.parent_id ? nodeMap[piece.parent_id] : null;
  const instruments = group?.instruments || [];
  const verChoices = pupitreChoices(group);

  const reloadPupitre = async (pid) => {
    const r = await api.get(`/files?node_id=${pid}`).catch(() => null);
    if (r) setFilesByPupitre((prev) => ({ ...prev, [pid]: r.data }));
  };

  const toggleFav = async (target, type, name, path) => {
    const favId = favorites[target];
    try {
      if (favId) await api.delete(`/favorites/${favId}`);
      else await api.post("/favorites", { target_type: type, target_id: target, name, path });
      loadFavs();
    } catch (e) { toast.error(e.friendly); }
  };

  // ---------- CRUD ----------

  const createCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const payload = { parent_id: null, kind: "category", name: newCatName.trim() };
      if (bothDisc && newCatDisc) payload.discipline = newCatDisc;
      await api.post("/library/nodes", payload);
      toast.success(t("lib.cat_created"));
      setNewCatName(""); setNewCatDisc(""); setCatOpen(false);
      loadTree();
    } catch (e) { toast.error(e.friendly); }
  };

  const savePiece = async (payload) => {
    if (!payload.name || !payload.parent_id) return;
    try {
      if (editingPiece) {
        await api.patch(`/library/nodes/${editingPiece.id}/details`, payload);
        toast.success(t("lib.piece_updated"));
        setPieceOpen(false);
        loadTree();
      } else {
        const { data } = await api.post("/library/nodes", { ...payload, kind: "piece" });
        toast.success(t("lib.piece_created"));
        setPieceOpen(false);
        loadTree();
        setPieceId(data.id);
      }
    } catch (e) { toast.error(e.friendly); }
  };

  const createPupitre = async (name) => {
    const n = String(name ?? pupName).trim();
    if (!n || !versionId) return;
    try {
      const { data } = await api.post("/library/nodes", { parent_id: versionId, kind: "pupitre", name: n });
      toast.success(t("lib.pupitre_added"));
      setPupOpen(false); setPupName("");
      loadTree();
      setPupitreId(data.id);
    } catch (e) { toast.error(e.friendly); }
  };

  const addCustomPupitre = () => {
    const v = verPupCustom.trim();
    if (v && !verPupitres.includes(v)) setVerPupitres([...verPupitres, v]);
    setVerPupCustom("");
  };

  const createVersion = async () => {
    if (!verName.trim()) return;
    try {
      const { data } = await api.post("/library/nodes", {
        parent_id: pieceId, kind: "version", name: verName.trim(), pupitres: verPupitres,
      });
      toast.success(t("lib.version_created"));
      setVerOpen(false); setVerName(""); setVerPupitres([]);
      loadTree();
      setVersionId(data.id);
    } catch (e) { toast.error(e.friendly); }
  };

  const rename = async () => {
    try {
      await api.put(`/library/nodes/${renameNode.id}`, { name: renameVal.trim() });
      toast.success(t("docs.renamed"));
      setRenameNode(null);
      loadTree();
    } catch (e) { toast.error(e.friendly); }
  };

  const remove = async () => {
    try {
      await api.delete(`/library/nodes/${delNode.id}`);
      toast.success(t("docs.deleted"));
      if (delNode.id === pieceId) setPieceId(null);
      if (delNode.id === versionId) setVersionId(null);
      if (delNode.id === categoryId) setCategoryId("all");
      setDelNode(null);
      loadTree();
    } catch (e) { toast.error(e.friendly); }
  };

  const uploadTo = async (pid, fileList) => {
    let ok = 0;
    const items = Array.from(fileList);
    const total = items.length;
    let i = 0;
    for (const item of items) {
      i++;
      const f = item.file || item;
      const name = (item.name || f.name).trim() || f.name;
      const toSend = name === f.name ? f : new File([f], name, { type: f.type });
      const fd = new FormData();
      fd.append("file", toSend);
      fd.append("context", "library");
      fd.append("kind", "auto");
      fd.append("node_id", pid);
      try {
        await api.post("/files/upload", fd, {
          onUploadProgress: (e) => setUploadProg({ name, pct: Math.round((e.loaded * 100) / (e.total || f.size || 1)), index: i, total }),
        });
        ok++;
      } catch (e) { toast.error(`${name} : ${e.friendly}`); }
    }
    setUploadProg(null);
    if (ok) toast.success(t("docs.import_done"));
    reloadPupitre(pid);
  };

  const submitRenameFile = async () => {
    if (!renameFileVal.trim()) return;
    try {
      await api.put(`/files/${renameFile.id}`, { name: renameFileVal.trim() });
      toast.success(t("docs.file_renamed"));
      setRenameFile(null);
      if (renameFile.pid) reloadPupitre(renameFile.pid);
    } catch (e) { toast.error(e.friendly); }
  };

  const addLink = async () => {
    try {
      await api.post("/files/link", { context: "library", node_id: linkFor, name: linkName, url: linkUrl });
      toast.success(t("docs.link_added"));
      setLinkFor(null); setLinkName(""); setLinkUrl("");
      reloadPupitre(linkFor);
    } catch (e) { toast.error(e.friendly); }
  };

  const deleteFile = async (f, pid) => {
    await api.delete(`/files/${f.id}`).catch((e) => toast.error(e.friendly));
    if (preview?.id === f.id) setPreview(null);
    reloadPupitre(pid);
  };

  // Télécharger (visuel, audio, vidéo) — le backend pose Content-Disposition: attachment
  const downloadFile = (f) => {
    const a = document.createElement("a");
    a.href = fileUrl(f.id, "download=1");
    a.download = f.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Partager : feuille de partage native si disponible, sinon copie du lien
  const shareFile = async (f) => {
    const url = fileUrl(f.id);
    if (navigator.share) {
      try {
        await navigator.share({ title: f.name, url });
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return; // annulé par l'utilisateur
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("lib.link_copied"));
    } catch {
      window.prompt(t("lib.share"), url);
    }
  };

  // Imprimer (visuels : PDF, images) via iframe masquée
  const printFile = (f) => {
    const ifr = document.createElement("iframe");
    ifr.style.display = "none";
    ifr.src = fileUrl(f.id);
    ifr.onload = () => {
      try {
        ifr.contentWindow.focus();
        ifr.contentWindow.print();
      } catch {
        window.open(fileUrl(f.id), "_blank");
      }
      setTimeout(() => ifr.remove(), 60000);
    };
    document.body.appendChild(ifr);
  };

  const FileRow = ({ f, pid }) => {
    const Icon = FILE_ICONS[f.kind] || FileText;
    const [actsOpen, setActsOpen] = useState(false);
    return (
      <div className="rounded-lg bg-white/[0.04] hover:bg-white/[0.07] transition-colors group" data-testid={`file-row-${f.id}`}>
        <div className="flex items-center gap-2 md:gap-3 px-2.5 md:px-3 py-2.5">
          <span className={`h-7 w-7 md:h-8 md:w-8 rounded-md flex items-center justify-center shrink-0 ${KIND_CHIP[f.kind] || KIND_CHIP.link}`}>
            <Icon className="h-3.5 w-3.5 md:h-4 md:w-4" />
          </span>
          <button className="flex-1 min-w-0 text-left text-xs md:text-sm truncate hover:underline" onClick={() => openFile(f)} data-testid={`file-open-${f.id}`}>
            {f.name}
          </button>
          <span className="text-xs t-secondary hidden md:block shrink-0" data-testid={`file-meta-${f.id}`}>{fmtSize(f.size, lang)}</span>
          {f.kind === "link" && <ExternalLink className="h-3.5 w-3.5 t-secondary shrink-0" />}
          <button
            onClick={() => setActsOpen((v) => !v)}
            className="p-1 -mr-1 t-secondary hover:text-white transition-colors shrink-0"
            data-testid={`file-actions-toggle-${f.id}`}
            aria-expanded={actsOpen}
            aria-label={t("usr.col_actions")}
            title={t("usr.col_actions")}
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${actsOpen ? "rotate-180" : ""}`} />
          </button>
        </div>
        {actsOpen && (
          <div className="flex items-center justify-end gap-1 px-2.5 md:px-3 pb-2" data-testid={`file-actions-${f.id}`}>
            {f.kind !== "link" && (
              <>
                <button onClick={() => downloadFile(f)} className="p-1.5 t-secondary hover:text-white transition-colors" data-testid={`file-download-${f.id}`} title={t("lib.download")} aria-label={t("lib.download")}>
                  <Download className="h-4 w-4" />
                </button>
                <button onClick={() => shareFile(f)} className="p-1.5 t-secondary hover:text-white transition-colors" data-testid={`file-share-${f.id}`} title={t("lib.share")} aria-label={t("lib.share")}>
                  <Share2 className="h-4 w-4" />
                </button>
                {f.kind === "partition" && (
                  <button onClick={() => printFile(f)} className="p-1.5 t-secondary hover:text-white transition-colors" data-testid={`file-print-${f.id}`} title={t("lib.print")} aria-label={t("lib.print")}>
                    <Printer className="h-4 w-4" />
                  </button>
                )}
              </>
            )}
            {f.kind !== "link" && <FileOfflineButton fileId={f.id} />}
            <button onClick={() => toggleFav(f.id, "file", f.name, `/bibliotheque?node=${pieceId}&open=${f.id}`)} className="p-1.5" data-testid={`fav-file-${f.id}`} aria-label={t("nav.favorites")} title={t("nav.favorites")}>
              <Heart className={`h-4 w-4 ${favorites[f.id] ? "fill-rose-400 text-rose-400" : "t-secondary hover:text-rose-400"}`} />
            </button>
            {canWrite && f.kind !== "link" && (
              <button onClick={() => { setRenameFile({ ...f, pid }); setRenameFileVal(f.name); }} className="p-1.5 t-secondary hover:text-white transition-colors" data-testid={`file-rename-${f.id}`} title={t("common.rename")} aria-label={t("common.rename")}>
                <Pencil className="h-4 w-4" />
              </button>
            )}
            {canWrite && (
              <button onClick={() => deleteFile(f, pid)} className="p-1.5 t-secondary hover:text-rose-400 transition-colors" data-testid={`file-delete-${f.id}`} title={t("common.delete")} aria-label={t("common.delete")}>
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // §1.6 : confirmation explicite avec nombre de fichiers avant suppression d'un pupitre
  const delFileCount = delNode?.kind === "pupitre" ? (filesByPupitre[delNode.id] || []).length : 0;
  const delDesc = !delNode
    ? ""
    : delNode.kind === "pupitre"
      ? delFileCount > 0
        ? t("lib.delete_pupitre_files", { name: delNode.name, count: delFileCount })
        : t("lib.delete_pupitre_empty", { name: delNode.name })
      : t("lib.delete_desc", { name: delNode.name });

  const catBtn = (id, label, testid) => (
    <button
      key={id}
      onClick={() => { setCategoryId(id); setPieceId(null); setHighlightId(null); }}
      className={`px-3 py-2 rounded-lg text-sm text-left transition-colors whitespace-nowrap ${
        categoryId === id ? "brand-bg text-white" : "t-secondary hover:bg-white/5"
      }`}
      data-testid={testid}
    >
      {label}
    </button>
  );

  // Swipe droite hiérarchique (mobile) : fichiers du morceau → liste des morceaux → tableau de bord.
  // Le drapeau nativeEvent empêche le listener global (Layout) de faire un navigate(-1) en double.
  const libSwipe = useRef(null);
  const onLibTouchStart = (e) => {
    libSwipe.current = null;
    if (!window.matchMedia("(max-width: 1023px)").matches) return;
    if (e.target.closest('input[type="range"], video, audio, iframe, [role="slider"], [role="dialog"]')) return;
    if (isHorizontalScrollZone(e.target)) return; // zone scrollable horizontalement : pas de retour arrière
    libSwipe.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onLibTouchEnd = (e) => {
    if (!libSwipe.current) return;
    const dx = e.changedTouches[0].clientX - libSwipe.current.x;
    const dy = Math.abs(e.changedTouches[0].clientY - libSwipe.current.y);
    libSwipe.current = null;
    if (dx < 70 || dy > 60) return;
    e.nativeEvent.__ranneaSwipeHandled = true;
    if (pieceId) setPieceId(null);           // fichiers du morceau → tous les morceaux
    else navigate("/tableau-de-bord");       // liste des morceaux → tableau de bord
  };

  return (
    <div className="flex gap-4 flex-1 min-h-0" data-testid="library-page" onTouchStart={onLibTouchStart} onTouchEnd={onLibTouchEnd}>
      {/* Catégories — desktop */}
      <aside className="hidden lg:flex w-52 shrink-0 app-card p-3 flex-col">
        <div className="flex items-center justify-between px-1 mb-2">
          <span className="text-xs font-bold uppercase tracking-wider t-secondary">{t("lib.categories")}</span>
          {canWrite && (
            <button onClick={() => setCatOpen(true)} className="brand hover:opacity-80" data-testid="add-category-button" title={t("lib.new_category")}>
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="space-y-0.5 overflow-y-auto flex-1" data-testid="categories-list">
          {catBtn("all", t("lib.all_pieces"), "category-all")}
          {bothDisc ? (
            ["bagad", "pipeband"].map((d) => (
              <div key={d}>
                <p className="text-[10px] font-bold uppercase tracking-wider accent px-3 pt-3 pb-1" data-testid={`zone-${d}`}>{t(d === "bagad" ? "lib.zone_bagad" : "lib.zone_pipeband")}</p>
                {catsFor(d).map((c) => (
                  <div key={`${d}-${c.id}`} className="flex items-center group">
                    <div className="flex-1">{catBtn(c.id, c.name, `category-${d}-${c.id}`)}</div>
                    {canWrite && (
                      <button
                        onClick={() => setDelNode(c)}
                        className="opacity-0 group-hover:opacity-100 t-secondary hover:text-rose-400 px-1"
                        data-testid={`delete-category-${d}-${c.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ))
          ) : (
            categories.map((c) => (
              <div key={c.id} className="flex items-center group">
                <div className="flex-1">{catBtn(c.id, c.name, `category-${c.id}`)}</div>
                {canWrite && (
                  <button
                    onClick={() => setDelNode(c)}
                    className="opacity-0 group-hover:opacity-100 t-secondary hover:text-rose-400 px-1"
                    data-testid={`delete-category-${c.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Colonne centrale */}
      <div className="flex-1 min-w-0 flex flex-col gap-4 overflow-y-auto overflow-x-clip">
        {/* Catégories — mobile */}
        {bothDisc && (
          <div className="lg:hidden flex gap-2" data-testid="discipline-mobile-filter">
            {[["all", "agd.filter_all"], ["bagad", "lib.zone_bagad"], ["pipeband", "lib.zone_pipeband"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setMobDisc(k)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${mobDisc === k ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
                data-testid={`mob-disc-${k}`}
              >
                {t(l)}
              </button>
            ))}
          </div>
        )}
        <div className="lg:hidden flex gap-2 overflow-x-auto pb-1 items-center" data-testid="categories-mobile">
          {catBtn("all", t("lib.all"), "category-all-mobile")}
          {(bothDisc && mobDisc !== "all" ? catsFor(mobDisc) : categories).map((c) => catBtn(c.id, c.name, `category-mobile-${c.id}`))}
          {canWrite && (
            <button onClick={() => setCatOpen(true)} className="brand shrink-0" data-testid="add-category-button-mobile">
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>

        {!piece ? (
          /* ---- Liste des morceaux ---- */
          <div className="app-card p-4" data-testid="pieces-panel">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <div className="relative flex-1 min-w-40">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 t-secondary" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("agd.piece_search")} className="pl-9 bg-transparent border-app h-10" data-testid="library-search-input" />
              </div>
              {canWrite && (
                <Button
                  className="brand-bg text-white"
                  onClick={() => { setPieceCat(categoryId !== "all" ? categoryId : categories[0]?.id || ""); setEditingPiece(null); setPieceOpen(true); }}
                  disabled={!categories.length}
                  data-testid="new-piece-button"
                >
                  <Plus className="h-4 w-4 mr-1" /> {t("lib.new_piece")}
                </Button>
              )}
            </div>
            {loadingTree && <p className="text-sm t-secondary py-6 text-center" data-testid="library-loading">{t("lib.loading")}</p>}
            {!loadingTree && piecesList.length === 0 && (
              <p className="text-sm t-secondary py-10 text-center" data-testid="pieces-empty">
                {t("lib.no_piece")}{canWrite && categories.length === 0 && t("lib.no_piece_create")}
              </p>
            )}
            <div className="space-y-1.5" data-testid="pieces-list">
              {piecesList.map((p) => (
                <div
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setPieceId(p.id); setHighlightId(null); }}
                  onKeyDown={(e) => e.key === "Enter" && (setPieceId(p.id), setHighlightId(null))}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/5 cursor-pointer transition-colors ${highlightId === p.id ? "bg-amber-400/10 ring-1 ring-inset ring-amber-400/60" : ""}`}
                  data-testid={`piece-item-${p.id}`}
                >
                  <span className="h-9 w-9 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0">
                    <Music2 className="h-4 w-4" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold uppercase tracking-wide truncate">{p.name}</span>
                    <span className="text-xs t-secondary">{nodeMap[p.parent_id]?.name || "—"}</span>
                  </span>
                  <span className="text-xs t-secondary hidden sm:block">{(childrenOf[p.id] || []).length} {t("lib.versions_count")}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFav(p.id, "piece", p.name, `/bibliotheque?node=${p.id}`); }}
                    data-testid={`fav-piece-${p.id}`}
                  >
                    <Heart className={`h-4 w-4 ${favorites[p.id] ? "fill-rose-400 text-rose-400" : "opacity-30 hover:opacity-100"}`} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* ---- Détail du morceau ---- */
          <>
            <div className="app-card p-4 lg:p-5" data-testid="piece-detail">
              {/* Titre + favori / modifier / supprimer */}
              <div className="flex items-center gap-1.5">
                <button onClick={() => setPieceId(null)} className="p-1.5 -ml-1.5 t-secondary hover:text-white shrink-0" data-testid="back-to-pieces" aria-label={t("lib.back_to_pieces")}>
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <h1 className="font-heading text-xl lg:text-2xl font-extrabold uppercase tracking-wide truncate flex-1" data-testid="piece-title">{piece.name}</h1>
                <button onClick={() => toggleFav(piece.id, "piece", piece.name, `/bibliotheque?node=${piece.id}`)} className="p-1.5 shrink-0" data-testid="piece-fav-button" aria-label={t("lib.piece_fav")}>
                  <Heart className={`h-5 w-5 ${favorites[piece.id] ? "fill-rose-400 text-rose-400" : "t-secondary hover:text-rose-400"}`} />
                </button>
                {canWrite && (
                  <>
                    <button onClick={() => { setEditingPiece(piece); setPieceOpen(true); }} className="p-1.5 t-secondary hover:text-white shrink-0" data-testid="edit-piece-button" aria-label={t("lib.edit_piece")}>
                      <Pencil className="h-5 w-5" />
                    </button>
                    <button onClick={() => setDelNode(piece)} className="p-1.5 text-rose-400 hover:text-rose-300 shrink-0" data-testid="delete-node-button" aria-label={t("lib.delete_piece")}>
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </>
                )}
              </div>

              {/* Catégorie + tempo + durée (+ compositeur) */}
              <div className="mt-3 card-bg border border-app rounded-2xl px-3.5 py-3 flex flex-wrap items-center gap-2" data-testid="piece-meta">
                {pieceCategory && <span className="accent-bg text-slate-900 text-xs font-semibold rounded-md px-2.5 py-1" data-testid="piece-meta-category">{pieceCategory.name}</span>}
                {piece.tempo_bpm ? <span className="bg-white/5 text-xs t-secondary rounded-md px-2.5 py-1" data-testid="piece-meta-tempo">{piece.tempo_bpm} BPM</span> : null}
                {piece.duration_sec ? <span className="bg-white/5 text-xs t-secondary rounded-md px-2.5 py-1" data-testid="piece-meta-duration">{Math.floor(piece.duration_sec / 60)}:{String(piece.duration_sec % 60).padStart(2, "0")}</span> : null}
                {piece.composer && <span className="bg-white/5 text-xs t-secondary rounded-md px-2.5 py-1" data-testid="piece-meta-composer">{piece.composer}</span>}
                {!pieceCategory && !piece.tempo_bpm && !piece.duration_sec && !piece.composer && <span className="text-xs t-secondary">{t("lib.no_meta")}</span>}
              </div>
              {piece.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="piece-tags">
                  {piece.tags.map((t) => (
                    <span key={t} className="text-xs border border-app rounded-full px-2 py-0.5 t-secondary">{t}</span>
                  ))}
                </div>
              )}
              {piece.description && <p className="text-sm t-secondary mt-3 whitespace-pre-wrap" data-testid="piece-description">{piece.description}</p>}
              {piece.history && <p className="text-sm t-secondary mt-2 whitespace-pre-wrap" data-testid="piece-history"><span className="font-medium">{t("lib.history_prefix")}</span>{piece.history}</p>}
            </div>

            {/* Versions : favori / renommer / supprimer / ajouter */}
            <div className="flex items-center justify-between mt-5 mb-2.5" data-testid="versions-header">
              <h2 className="font-heading text-lg font-bold">{t("fav.type_version")}</h2>
              <div className="flex items-center gap-0.5">
                {versionId && nodeMap[versionId] && (
                  <>
                    <button onClick={() => toggleFav(versionId, "version", `${piece.name} — ${nodeMap[versionId].name}`, `/bibliotheque?node=${versionId}`)} className="p-1.5" data-testid="version-fav-button" aria-label={t("lib.version_fav")}>
                      <Heart className={`h-5 w-5 ${favorites[versionId] ? "fill-rose-400 text-rose-400" : "t-secondary hover:text-rose-400"}`} />
                    </button>
                    {canWrite && (
                      <>
                        <button className="p-1.5 t-secondary hover:text-white" onClick={() => { setRenameNode(nodeMap[versionId]); setRenameVal(nodeMap[versionId].name); }} data-testid="rename-version-button" aria-label={t("lib.rename_version")}>
                          <Pencil className="h-5 w-5" />
                        </button>
                        <button className="p-1.5 text-rose-400 hover:text-rose-300" onClick={() => setDelNode(nodeMap[versionId])} data-testid="delete-version-button" aria-label={t("lib.delete_version")}>
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </>
                    )}
                  </>
                )}
                {canWrite && (
                  <button className="accent text-sm font-semibold flex items-center gap-1 px-2 py-1.5" onClick={() => { setVerName(""); setVerPupitres(presetPupitres(group)); setVerOpen(true); }} data-testid="add-version-button">
                    <Plus className="h-4 w-4" /> {t("common.add")}
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="version-chips">
              {versions.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setVersionId(v.id)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    v.id === versionId ? "accent-bg text-slate-900" : "border border-app t-secondary hover:bg-white/5"
                  }`}
                  data-testid={`version-chip-${v.id}`}
                >
                  {v.name}
                </button>
              ))}
            </div>

            {!versionId && (
              <p className="t-secondary text-sm app-card p-6 text-center" data-testid="no-version-hint">
                {t("lib.no_version")}{canWrite && t("lib.no_version_hint")}
              </p>
            )}

            {/* Onglets pupitres (scroll horizontal) + ajout après création */}
            {versionId && pupitres.length > 0 && (
              <div className="flex gap-2 overflow-x-auto py-1 -mx-1 px-1" data-testid="pupitre-tabs">
                {pupitres.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPupitreId(p.id)}
                    className={`px-5 py-2 rounded-full text-sm whitespace-nowrap shrink-0 transition-colors ${
                      p.id === pupitreId ? "accent-bg text-slate-900 font-semibold" : "border border-app t-secondary hover:bg-white/5"
                    }`}
                    data-testid={`pupitre-tab-${p.id}`}
                  >
                    {p.name}
                  </button>
                ))}
                {canWrite && (
                  <button
                    onClick={() => { setPupName(""); setPupOpen(true); }}
                    className="px-5 py-2 rounded-full text-sm whitespace-nowrap shrink-0 border border-dashed accent-border accent hover:bg-white/5"
                    data-testid="add-pupitre-button"
                  >
                    {t("lib.pupitre_add")}
                  </button>
                )}
              </div>
            )}
            {versionId && pupitres.length === 0 && canWrite && (
              <button
                onClick={() => { setPupName(""); setPupOpen(true); }}
                className="px-5 py-2 rounded-full text-sm border border-dashed accent-border accent hover:bg-white/5"
                data-testid="add-pupitre-button"
              >
                {t("lib.pupitre_add")}
              </button>
            )}

            {/* Pupitre sélectionné : contenu répertorié par classe */}
            {pup && (
              <section
                className={`app-card p-4 lg:p-5 transition-shadow ${dragOver === pup.id ? "ring-2 ring-emerald-400" : ""}`}
                data-testid={`pupitre-section-${pup.id}`}
                onDragOver={canWrite ? (e) => { e.preventDefault(); setDragOver(pup.id); } : undefined}
                onDragLeave={canWrite ? () => setDragOver(null) : undefined}
                onDrop={canWrite ? (e) => { e.preventDefault(); setDragOver(null); if (e.dataTransfer.files?.length) uploadTo(pup.id, e.dataTransfer.files); } : undefined}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-lg font-bold" data-testid={`pupitre-name-${pup.id}`}>{t("lib.pupitre_prefix")} {pup.name}</h3>
                  {canWrite && (
                    <div className="flex items-center gap-0.5">
                      <button className="p-1.5 t-secondary hover:text-white" onClick={() => setLinkFor(pup.id)} data-testid={`pupitre-link-${pup.id}`} aria-label={t("docs.add_link")} title={t("docs.add_link")}>
                        <Link2 className="h-4 w-4" />
                      </button>
                      <button className="p-1.5 t-secondary hover:text-white" onClick={() => { setRenameNode(pup); setRenameVal(pup.name); }} data-testid={`pupitre-rename-${pup.id}`} aria-label={t("lib.rename_pupitre")}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button className="p-1.5 text-rose-400 hover:text-rose-300" onClick={() => setDelNode(pup)} data-testid={`pupitre-delete-${pup.id}`} aria-label={t("lib.remove_pupitre")}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div data-testid={`pupitre-files-${pup.id}`}>
                  {FILE_CLASSES.map((cls) => {
                    const list = (filesByPupitre[pup.id] || []).filter((f) => f.kind === cls.kind);
                    if (cls.kind === "link" && !list.length) return null;
                    return (
                      <div key={cls.kind} className="mb-4 last:mb-0" data-testid={`class-section-${cls.kind}-${pup.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-sm flex items-center gap-2">
                            <cls.icon className="h-4 w-4 t-secondary" /> {t(cls.label)} <span className="t-secondary text-xs font-normal">({list.length})</span>
                          </p>
                          {canWrite && cls.accept && (
                            <label className="accent text-sm font-medium cursor-pointer hover:underline flex items-center gap-1" data-testid={`class-add-${cls.kind}-${pup.id}`}>
                              <input type="file" multiple accept={cls.accept} className="hidden" onChange={(e) => { if (e.target.files?.length) setPendingUpload({ pid: pup.id, items: Array.from(e.target.files).map((f) => ({ file: f, name: f.name })) }); e.target.value = ""; }} data-testid={`class-upload-${cls.kind}-${pup.id}`} />
                              <Plus className="h-4 w-4" /> {t("common.add")}
                            </label>
                          )}
                        </div>
                        <div className="space-y-2" data-testid={`class-files-${cls.kind}-${pup.id}`}>
                          {list.map((f) => <FileRow key={f.id} f={f} pid={pup.id} />)}
                          {!list.length && <p className="text-xs t-secondary">{t("lib.no_file")}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {/* Visualiseur persistant — desktop */}
      <aside className="hidden xl:flex w-[400px] shrink-0 app-card overflow-hidden flex-col" data-testid="media-preview-panel">
        {preview ? (
          preview.kind === "partition" ? (
            <div className="flex flex-col h-full fullscreen:bg-black" data-testid="preview-pdf-wrap">
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-app">
                <span className="text-sm font-medium truncate">{preview.name}</span>
                <span className="flex items-center gap-1 shrink-0">
                  <button onClick={() => viewerFullscreen("preview-pdf-wrap", preview)} className="t-secondary hover:text-white" data-testid="preview-fullscreen-button" title={t("docs.fullscreen")}>
                    <Maximize2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => setPreview(null)} className="t-secondary hover:text-white shrink-0" data-testid="preview-close">
                    <X className="h-4 w-4" />
                  </button>
                </span>
              </div>
              <iframe src={fileUrl(preview.id)} title={preview.name} className="flex-1 w-full bg-white" data-testid="preview-pdf-frame" />
            </div>
          ) : (
            <div className="p-3 overflow-y-auto">
              <MediaPlayer file={preview} inline onClose={() => setPreview(null)} />
            </div>
          )
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center t-secondary gap-3 p-6" data-testid="media-preview-empty">
            <Eye className="h-10 w-10 opacity-30" />
            <p className="text-sm text-center">{t("lib.preview_empty_1")}<br />{t("lib.preview_empty_2")}</p>
          </div>
        )}
      </aside>

      {/* Lecteurs modaux — mobile/tablette */}
      {pdf && (
        <Dialog open={true} onOpenChange={() => setPdf(null)}>
          <DialogContent hideClose className="card-bg border-app max-w-5xl h-[90vh] flex flex-col fullscreen:h-full fullscreen:max-w-none fullscreen:w-screen" data-testid="pdf-viewer-dialog">
            <DialogHeader className="flex-row items-center justify-between gap-2 space-y-0">
              <DialogTitle className="font-heading truncate">{pdf.name}</DialogTitle>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => viewerFullscreen("pdf-viewer-dialog", pdf)} className="t-secondary hover:text-white p-2" data-testid="pdf-fullscreen-button" title={t("docs.fullscreen")}>
                  <Maximize2 className="h-4 w-4" />
                </button>
                <button onClick={closePdf} className="t-secondary hover:text-white p-2" data-testid="pdf-close-button" title={t("common.close")} aria-label={t("common.close")}>
                  <X className="h-5 w-5" />
                </button>
              </div>
              <DialogDescription className="sr-only">{t("lib.pdf_reader_sr")}</DialogDescription>
            </DialogHeader>
            <iframe src={fileUrl(pdf.id)} title={pdf.name} className="flex-1 w-full rounded-lg bg-white" data-testid="pdf-viewer-frame" />
          </DialogContent>
        </Dialog>
      )}
      {player && <MediaPlayer file={player} onClose={() => setPlayer(null)} />}

      {/* ---- Dialogs ---- */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="card-bg border-app" data-testid="add-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("lib.new_category")}</DialogTitle>
            <DialogDescription className="sr-only">{t("lib.new_category_sr")}</DialogDescription>
          </DialogHeader>
          <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder={t("lib.category_name_placeholder")} className="bg-transparent border-app" data-testid="new-category-name-input" autoFocus />
          {bothDisc && (
            <div>
              <p className="text-xs uppercase tracking-wider t-secondary mb-1.5">{t("lib.discipline_label")}</p>
              <div className="flex gap-2 flex-wrap" data-testid="category-discipline-chips">
                {[["", "lib.discipline_common"], ["bagad", "lib.zone_bagad"], ["pipeband", "lib.zone_pipeband"]].map(([v, l]) => (
                  <button
                    type="button"
                    key={v || "common"}
                    onClick={() => setNewCatDisc(v)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${newCatDisc === v ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
                    data-testid={`cat-disc-${v || "common"}`}
                  >
                    {t(l)}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Button className="brand-bg text-white" onClick={createCategory} disabled={!newCatName.trim()} data-testid="create-category-confirm">{t("common.create")}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={pupOpen} onOpenChange={setPupOpen}>
        <DialogContent className="card-bg border-app" data-testid="add-pupitre-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("lib.new_pupitre")}</DialogTitle>
            <DialogDescription className="sr-only">{t("lib.new_pupitre_sr")}</DialogDescription>
          </DialogHeader>
          {instruments.filter((i) => !pupitres.some((p) => p.name === i)).length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider t-secondary mb-2">{t("lib.group_instruments")}</p>
              <div className="flex flex-wrap gap-2" data-testid="pupitre-instrument-chips">
                {instruments.filter((i) => !pupitres.some((p) => p.name === i)).map((i) => (
                  <button
                    key={i}
                    onClick={() => createPupitre(i)}
                    className="px-4 py-2 rounded-full text-sm border border-app t-secondary hover:bg-white/5"
                    data-testid={`pupitre-instrument-${i}`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Input value={pupName} onChange={(e) => setPupName(e.target.value)} placeholder={t("lib.custom_name")} className="bg-transparent border-app" data-testid="pupitre-name-input" autoFocus />
            <Button className="brand-bg text-white shrink-0" onClick={() => createPupitre()} disabled={!pupName.trim()} data-testid="create-pupitre-confirm">{t("common.add")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {pieceOpen && (
        <PieceFormSheet
          categories={categories}
          initial={editingPiece || (pieceCat ? { parent_id: pieceCat } : null)}
          onClose={() => setPieceOpen(false)}
          onSubmit={savePiece}
        />
      )}

      <Dialog open={verOpen} onOpenChange={setVerOpen}>
        <DialogContent className="card-bg border-app" data-testid="add-version-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("lib.new_version")}</DialogTitle>
            <DialogDescription className="t-secondary text-sm">{t("lib.new_version_desc")}</DialogDescription>
          </DialogHeader>
          <Input value={verName} onChange={(e) => setVerName(e.target.value)} placeholder={t("lib.version_name_placeholder")} className="bg-transparent border-app" data-testid="version-name-input" autoFocus />
          <div>
            <p className="text-xs uppercase tracking-wider t-secondary mb-2">{t("lib.version_pupitres_label")}</p>
            {verChoices.length === 0 ? (
              <p className="text-xs t-secondary">{t("lib.no_instruments_hint")}</p>
            ) : (
              <div className="grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto" data-testid="pupitre-checkboxes">
                {verChoices.map((i) => (
                  <label key={i} className="flex items-center gap-2 text-sm cursor-pointer border border-app rounded-lg px-3 py-2 hover:bg-white/5">
                    <input
                      type="checkbox"
                      className="accent-emerald-500"
                      checked={verPupitres.includes(i)}
                      onChange={(e) => setVerPupitres(e.target.checked ? [...verPupitres, i] : verPupitres.filter((x) => x !== i))}
                      data-testid={`pupitre-check-${i}`}
                    />
                    {i}
                  </label>
                ))}
              </div>
            )}
            {verPupitres.filter((p) => !verChoices.includes(p)).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="version-custom-pupitres">
                {verPupitres.filter((p) => !verChoices.includes(p)).map((p) => (
                  <span key={p} className="inline-flex items-center gap-1.5 border border-app rounded-full px-3 py-1.5 text-sm">
                    {p}
                    <button type="button" onClick={() => setVerPupitres(verPupitres.filter((x) => x !== p))} className="t-secondary hover:text-rose-400" data-testid={`version-custom-pupitre-remove-${p}`} aria-label={t("lib.remove_named", { name: p })}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <Input
                value={verPupCustom}
                onChange={(e) => setVerPupCustom(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomPupitre(); } }}
                placeholder={t("lib.custom_pupitre_placeholder")}
                className="bg-transparent border-app"
                data-testid="version-custom-pupitre-input"
              />
              <Button variant="outline" className="border-app shrink-0" onClick={addCustomPupitre} disabled={!verPupCustom.trim()} data-testid="version-custom-pupitre-add">{t("common.add")}</Button>
            </div>
          </div>
          <Button className="brand-bg text-white" onClick={createVersion} disabled={!verName.trim()} data-testid="create-version-confirm">
            {t("lib.create_version")}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingUpload} onOpenChange={(v) => !v && setPendingUpload(null)}>
        <DialogContent className="card-bg border-app" data-testid="upload-names-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("docs.upload_names_title")}</DialogTitle>
            <DialogDescription className="sr-only">{t("docs.upload_names_sr")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(pendingUpload?.items || []).map((it, i) => (
              <Input key={i} value={it.name} onChange={(e) => setPendingUpload((p) => ({ ...p, items: p.items.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) }))} className="bg-transparent border-app" data-testid={`upload-name-input-${i}`} />
            ))}
          </div>
          <Button className="w-full brand-bg text-white" onClick={async () => { const p = pendingUpload; setPendingUpload(null); await uploadTo(p.pid, p.items); }} data-testid="upload-names-confirm">{t("docs.import")}</Button>
        </DialogContent>
      </Dialog>

      {uploadProg && (
        <div className="fixed bottom-20 md:bottom-6 right-4 left-4 md:left-auto md:w-80 app-card p-4 z-50 anim-fade-up" data-testid="upload-progress-card">
          <div className="flex items-center justify-between text-xs mb-2 gap-2">
            <span className="truncate font-medium">{uploadProg.name}</span>
            <span className="accent font-bold shrink-0" data-testid="upload-progress-pct">{uploadProg.pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full accent-bg transition-all duration-200" style={{ width: `${uploadProg.pct}%` }} data-testid="upload-progress-bar" />
          </div>
          {uploadProg.total > 1 && <p className="text-[10px] t-secondary mt-1.5">{t("docs.file_counter", { i: uploadProg.index, total: uploadProg.total })}</p>}
        </div>
      )}

      <Dialog open={!!renameFile} onOpenChange={(v) => !v && setRenameFile(null)}>
        <DialogContent className="card-bg border-app" data-testid="rename-file-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("docs.rename_file_title")}</DialogTitle>
            <DialogDescription className="sr-only">{t("docs.rename_file_title")}</DialogDescription>
          </DialogHeader>
          <Input value={renameFileVal} onChange={(e) => setRenameFileVal(e.target.value)} className="bg-transparent border-app" data-testid="rename-file-input" autoFocus onKeyDown={(e) => e.key === "Enter" && submitRenameFile()} />
          <Button className="w-full brand-bg text-white" onClick={submitRenameFile} data-testid="rename-file-save">{t("common.rename")}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameNode} onOpenChange={(v) => !v && setRenameNode(null)}>
        <DialogContent className="card-bg border-app" data-testid="rename-node-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("common.rename")}</DialogTitle>
            <DialogDescription className="sr-only">{t("lib.rename_node_sr")}</DialogDescription>
          </DialogHeader>
          <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="bg-transparent border-app" data-testid="rename-node-input" autoFocus />
          <Button className="brand-bg text-white" onClick={rename} disabled={!renameVal.trim()} data-testid="rename-node-confirm">{t("common.save")}</Button>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delNode}
        title={t("usr.delete_title")}
        description={delDesc}
        onConfirm={remove}
        onClose={() => setDelNode(null)}
        testid="delete-node-confirm-dialog"
      />

      <Dialog open={!!linkFor} onOpenChange={(v) => !v && setLinkFor(null)}>
        <DialogContent className="card-bg border-app" data-testid="add-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("docs.add_link")}</DialogTitle>
            <DialogDescription className="sr-only">{t("lib.add_link_sr")}</DialogDescription>
          </DialogHeader>
          <Input value={linkName} onChange={(e) => setLinkName(e.target.value)} placeholder={t("docs.link_name")} className="bg-transparent border-app" data-testid="link-name-input" />
          <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://youtube.com/…" className="bg-transparent border-app" data-testid="link-url-input" />
          <Button className="brand-bg text-white" onClick={addLink} disabled={!linkName.trim() || !linkUrl.trim()} data-testid="add-link-confirm">{t("common.add")}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
